<template>
	<label class="subject-field">
		<span class="field-label">{{ title }}</span>
		<EditableField
			:name="field.key"
			:title="title"
			hide-title
			:type="field.type"
			:value="displayValue(field)"
			:options="fieldOptions(field)"
			:multiple="field.multiple"
			:debounce-ms="0"
			:readonly="readonly"
			:disabled="readonly"
			:symbol="field.area ? areaUnitSymbol : undefined"
			@input="update(field, $event.value)"
		/>
	</label>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n"
import { LandSubcategory } from "@/graphql"
import type { ComparableAnalysisSubjectInput, TransactionParkingType } from "@/graphql"
import EditableField from "@/components/transactions/EditableField.vue"
import type { ComparableAnalysisField } from "@/utils/comparableAnalysisFields"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AreaUnit, convertArea } from "@/utils/unitSystem"
import { normalizeParkingSelection } from "@/utils/transactionParking"
const value = defineModel<ComparableAnalysisSubjectInput>({ required: true })
const props = defineProps<{ field: ComparableAnalysisField; title: string; readonly?: boolean }>()
const { t } = useI18n()
const { areaUnit, areaUnitSymbol } = useUnitSystem()
function fieldOptions(field: ComparableAnalysisField) {
	const prefix =
		field.key === "assetType"
			? "assetType"
			: field.key === "landCategoryNew"
				? "landCategory"
				: field.key === "landSubcategory"
					? "landSubcategory"
					: null
	return prefix
		? field.options?.map(value => ({
				value,
				label: t(
					`property.${prefix}.${prefix === "landSubcategory" ? Object.entries(LandSubcategory).find(([, item]) => item === value)?.[0] ?? value : value}`
				),
			}))
		: field.options
}
function displayValue(field: ComparableAnalysisField) {
	const source = value.value[field.key]
	return field.area && typeof source === "number" ? convertArea(source, AreaUnit.SquareFeet, areaUnit.value) : source
}
function update(field: ComparableAnalysisField, input: unknown) {
	if (props.readonly) return
	if (field.key === "parkingTypes" && Array.isArray(input)) {
		value.value = {
			...value.value,
			parkingTypes: normalizeParkingSelection(value.value.parkingTypes, input as TransactionParkingType[]),
		}
		return
	}
	if (field.key === "deductionFiles" && typeof input === "string") {
		value.value = {
			...value.value,
			deductionFiles: input
				.split(",")
				.map(value => value.trim())
				.filter(Boolean),
		}
		return
	}
	const next = field.area && typeof input === "number" ? convertArea(input, areaUnit.value, AreaUnit.SquareFeet) : input
	const subject = { ...value.value, [field.key]: next }
	if (field.key === "assetType" && Array.isArray(next) && !next.includes("land")) {
		subject.landCategoryNew = []
		subject.landSubcategory = []
	}
	if (
		["landCategoryNew", "landSubcategory"].includes(field.key) &&
		Array.isArray(next) &&
		next.length &&
		!subject.assetType?.includes("land")
	)
		subject.assetType = [...(subject.assetType ?? []), "land"]
	value.value = subject
}
</script>
<style scoped>
.subject-field {
	display: block;
	:deep(.editable-field) {
		min-width: 0;
	}
	.field-label {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip-path: inset(50%);
	}
}
</style>
